import { useState } from 'react'
import { Bot, ChevronDown, ChevronUp, FileText } from 'lucide-react'

export interface BotSummaryCardProps {
  summary: string
  createdAt?: string
  onOpenNotes?: () => void
  defaultExpanded?: boolean
  maxLength?: number
}

export function BotSummaryCard({
  summary,
  createdAt,
  onOpenNotes,
  defaultExpanded = false,
  maxLength = 220,
}: BotSummaryCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const cleanSummary = (summary || '').trim()
  if (!cleanSummary) return null

  const isTruncated = cleanSummary.length > maxLength
  const visibleText = isExpanded || !isTruncated
    ? cleanSummary
    : `${cleanSummary.slice(0, maxLength).trim()}...`

  return (
    <div
      role="region"
      aria-label="Resumen de IA del cliente"
      className="mx-auto mb-4 max-w-5xl overflow-hidden rounded-2xl border border-violet-200/90 bg-gradient-to-br from-violet-50/70 via-white to-amber-50/40 p-3 sm:p-4 shadow-2xs backdrop-blur-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-violet-100 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100/80 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-violet-800">
            <Bot className="h-3.5 w-3.5 text-violet-600" />
            Resumen del Bot
          </span>
          {createdAt && (
            <span className="text-[11px] text-slate-500 font-medium">
              {createdAt}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isTruncated && (
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-violet-700 hover:bg-violet-50 active:scale-95 transition-all"
            >
              <span>{isExpanded ? 'Ver menos' : 'Ver resumen'}</span>
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}

          {onOpenNotes && (
            <button
              type="button"
              onClick={onOpenNotes}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all"
            >
              <FileText className="h-3 w-3" />
              <span>Abrir notas</span>
            </button>
          )}
        </div>
      </div>

      <div className="mt-2.5 rounded-xl bg-white/90 p-3 shadow-2xs border border-violet-100/70">
        <p className="whitespace-pre-wrap break-words text-xs sm:text-sm leading-relaxed text-slate-700">
          {visibleText}
        </p>

        {isTruncated && !isExpanded && (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="mt-2 text-xs font-semibold text-violet-700 hover:text-violet-800 transition-colors"
          >
            Mostrar el resumen completo
          </button>
        )}
      </div>
    </div>
  )
}
